import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ButtonBase from '@mui/material/ButtonBase';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SportsHandballRoundedIcon from '@mui/icons-material/SportsHandballRounded';
import AppLayout from '../../components/ui/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import LoadingState from '../../components/ui/LoadingState';
import EmptyState from '../../components/ui/EmptyState';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import { IconeLance, nomeCurto } from '../../components/Lances';
import { BOTTOM_NAV_HEIGHT } from '../../components/BottomNav';
import { dataPelada } from './PeladasPage';
import api from '../../api';
import tokens from '../../theme/tokens';

const CAMPOS = [
  { campo: 'gols', tipo: 'gol', label: 'Gols' },
  { campo: 'assistencias', tipo: 'assistencia', label: 'Assistências' },
  { campo: 'amarelos', tipo: 'amarelo', label: 'Cartão amarelo' },
  { campo: 'azuis', tipo: 'azul', label: 'Cartão azul' },
  { campo: 'vermelhos', tipo: 'vermelho', label: 'Cartão vermelho' },
];

const igual = (a, b) => CAMPOS.every(({ campo }) => (a[campo] || 0) === (b[campo] || 0));

// Linha de uma estatística: ícone e nome à esquerda, [−] valor [+] à direita (alvos grandes para o dedo)
const Contador = ({ tipo, label, valor, onChange }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.5 }}>
    <Box sx={{ width: 24, display: 'grid', placeItems: 'center' }}><IconeLance tipo={tipo} /></Box>
    <Typography variant="body2" sx={{ flexGrow: 1 }}>{label}</Typography>
    <IconButton aria-label={`Diminuir ${label}`} onClick={() => onChange(Math.max(0, valor - 1))} disabled={valor === 0} sx={{ width: 40, height: 40, border: 1, borderColor: 'divider' }}>
      <RemoveRoundedIcon />
    </IconButton>
    <Typography sx={{ width: 28, textAlign: 'center', fontWeight: 700, fontSize: '1.15rem' }}>{valor}</Typography>
    <IconButton aria-label={`Aumentar ${label}`} onClick={() => onChange(Math.min(99, valor + 1))} sx={{ width: 40, height: 40, bgcolor: tokens.color.brandSoft, color: tokens.color.brand, '&:hover': { bgcolor: tokens.color.brandSoft } }}>
      <AddRoundedIcon />
    </IconButton>
  </Stack>
);

// Resumo compacto ao lado do nome (só o que for maior que zero)
const Resumo = ({ valores }) => {
  const itens = CAMPOS.filter(({ campo }) => valores[campo] > 0);
  if (itens.length === 0) return <Typography variant="caption" color="text.disabled">Sem lances</Typography>;
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {itens.map(({ campo, tipo }) => (
        <Stack key={campo} direction="row" spacing={0.25} alignItems="center">
          <IconeLance tipo={tipo} />
          <Typography variant="caption" fontWeight={700}>{valores[campo]}</Typography>
        </Stack>
      ))}
    </Stack>
  );
};

/** Estatísticas de uma pelada (Admin/Diretor): gols, assistências e cartões por jogador e gols sofridos por goleiro, com salvar em lote. */
const PeladaEstatisticasPage = () => {
  const { peladaId } = useParams();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [edicao, setEdicao] = useState({}); // user_name -> totais editados
  const [aberto, setAberto] = useState(null); // user_name expandido
  const [busca, setBusca] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [toast, setToast] = useState(null);

  const [edicaoGoleiros, setEdicaoGoleiros] = useState({}); // nome -> gols sofridos editados

  const aplicar = (resposta) => {
    setDados(resposta);
    setEdicao({});
    setEdicaoGoleiros({});
  };

  useEffect(() => {
    api
      .get(`peladas/${peladaId}/estatisticas`)
      .then((r) => aplicar(r.data))
      .catch((e) => setErro(e.response?.data?.error || 'Não foi possível carregar a pelada.'));
  }, [peladaId]);

  const valores = (j) => edicao[j.user_name] || j.total;
  const alteradosJogadores = dados ? dados.jogadores.filter((j) => edicao[j.user_name] && !igual(edicao[j.user_name], j.total)) : [];
  const golsDoGoleiro = (g) => (edicaoGoleiros[g.nome] !== undefined ? edicaoGoleiros[g.nome] : g.total);
  const alteradosGoleiros = dados ? (dados.goleiros || []).filter((g) => golsDoGoleiro(g) !== g.total) : [];
  const alterados = [...alteradosJogadores, ...alteradosGoleiros];

  const mudar = (j, campo, valor) => setEdicao((atual) => ({ ...atual, [j.user_name]: { ...valores(j), [campo]: valor } }));

  const salvar = async () => {
    setSalvando(true);
    try {
      const response = await api.put(`peladas/${peladaId}/estatisticas`, {
        jogadores: alteradosJogadores.map((j) => ({ user_name: j.user_name, total: edicao[j.user_name] })),
        goleiros: alteradosGoleiros.map((g) => ({ goleiro: g.goleiro, nome: g.nome, golsSofridos: golsDoGoleiro(g) })),
      });
      aplicar(response.data);
      setAberto(null);
      setToast({ severity: 'success', message: 'Estatísticas salvas.' });
    } catch (e) {
      setToast({ severity: 'error', message: e.response?.data?.error || 'Não foi possível salvar.' });
    }
    setSalvando(false);
  };

  const lista = useMemo(() => {
    if (!dados) return [];
    const termo = busca.trim().toLowerCase();
    return termo ? dados.jogadores.filter((j) => j.user_name.toLowerCase().includes(termo)) : dados.jogadores;
  }, [dados, busca]);

  const totais = dados
    ? CAMPOS.reduce((o, { campo }) => ({ ...o, [campo]: dados.jogadores.reduce((t, j) => t + (valores(j)[campo] || 0), 0) }), {})
    : null;

  return (
    <AppLayout maxWidth="sm">
      <PageHeader
        backTo="/peladas"
        eyebrow="Peladas"
        title={dados ? dataPelada(dados.pelada.date, { weekday: 'short', day: '2-digit', month: 'short' }) : 'Pelada'}
        subtitle={dados ? `${dados.pelada.time ? `${dados.pelada.time} · ` : ''}${dados.pelada.partidas} partidas · ${dados.jogadores.length} jogadores` : undefined}
      />

      {erro ? (
        <Card><EmptyState title={erro} /></Card>
      ) : !dados ? (
        <LoadingState label="Carregando estatísticas..." />
      ) : (
        <>
          {/* Totais da pelada */}
          <Card sx={{ p: 1.5, mb: 2 }}>
            <Stack direction="row" justifyContent="space-around">
              {CAMPOS.map(({ campo, tipo, label }) => (
                <Stack key={campo} alignItems="center" spacing={0.25} title={label}>
                  <IconeLance tipo={tipo} />
                  <Typography fontWeight={700}>{totais[campo]}</Typography>
                </Stack>
              ))}
            </Stack>
          </Card>

          {dados.jogadores.length > 8 && (
            <TextField
              size="small"
              fullWidth
              placeholder="Buscar jogador"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              sx={{ mb: 1.5, bgcolor: 'background.paper' }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
            />
          )}

          {dados.jogadores.length === 0 ? (
            <Card><EmptyState title="Nenhum jogador fez check-in nesta pelada" /></Card>
          ) : (
            <Stack spacing={1}>
              <Typography variant="caption" color="text.secondary">
                Toque no jogador para lançar. Os números já incluem os lances registrados nas partidas.
              </Typography>
              {lista.map((j) => {
                const v = valores(j);
                const mudou = edicao[j.user_name] && !igual(edicao[j.user_name], j.total);
                const expandido = aberto === j.user_name;
                return (
                  <Card key={j.user_name} sx={{ borderColor: mudou ? tokens.color.info : undefined, bgcolor: mudou ? tokens.color.infoSoft : undefined }}>
                    <ButtonBase onClick={() => setAberto(expandido ? null : j.user_name)} sx={{ width: '100%', textAlign: 'left', px: 1.5, py: 1.25 }}>
                      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ width: '100%', minWidth: 0 }}>
                        <PlayerAvatar userId={j.guest ? null : j.user} name={j.user_name} size={40} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <Typography fontWeight={600} noWrap>{nomeCurto(j.user_name)}</Typography>
                            {j.guest && <Chip size="small" label="Convidado" variant="outlined" sx={{ height: 20 }} />}
                          </Stack>
                          <Resumo valores={v} />
                        </Box>
                        <ExpandMoreRoundedIcon sx={{ color: 'text.secondary', transform: expandido ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
                      </Stack>
                    </ButtonBase>
                    <Collapse in={expandido} unmountOnExit>
                      <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
                        {CAMPOS.map(({ campo, tipo, label }) => (
                          <Contador key={campo} tipo={tipo} label={label} valor={v[campo] || 0} onChange={(valor) => mudar(j, campo, valor)} />
                        ))}
                      </Box>
                    </Collapse>
                  </Card>
                );
              })}
              {lista.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Nenhum jogador encontrado.</Typography>}
            </Stack>
          )}

          {/* Gols sofridos por goleiro (escalados na pelada ou que jogaram as partidas) */}
          <Card sx={{ p: 1.5, mt: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <SportsHandballRoundedIcon color="primary" />
              <Typography fontWeight={700}>Goleiros · gols sofridos</Typography>
            </Stack>
            {(dados.goleiros || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                Nenhum goleiro escalado nesta pelada. Escale na tela de Confirmação (Escala da pelada).
              </Typography>
            ) : (
              <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                {dados.goleiros.map((g) => {
                  const valor = golsDoGoleiro(g);
                  const mudou = valor !== g.total;
                  return (
                    <Box key={g.goleiro || g.nome} sx={{ py: 0.5, bgcolor: mudou ? tokens.color.infoSoft : 'transparent', borderRadius: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <PlayerAvatar name={g.nome} size={32} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{nomeCurto(g.nome)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {g.calculado ? `${g.calculado} pelas partidas registradas` : 'Sem gols registrados nas partidas'}
                          </Typography>
                        </Box>
                        <IconButton aria-label={`Diminuir gols sofridos de ${g.nome}`} onClick={() => setEdicaoGoleiros((a) => ({ ...a, [g.nome]: Math.max(0, valor - 1) }))} disabled={valor === 0} sx={{ width: 40, height: 40, border: 1, borderColor: 'divider' }}>
                          <RemoveRoundedIcon />
                        </IconButton>
                        <Typography sx={{ width: 28, textAlign: 'center', fontWeight: 700, fontSize: '1.15rem' }}>{valor}</Typography>
                        <IconButton aria-label={`Aumentar gols sofridos de ${g.nome}`} onClick={() => setEdicaoGoleiros((a) => ({ ...a, [g.nome]: Math.min(99, valor + 1) }))} sx={{ width: 40, height: 40, bgcolor: tokens.color.brandSoft, color: tokens.color.brand, '&:hover': { bgcolor: tokens.color.brandSoft } }}>
                          <AddRoundedIcon />
                        </IconButton>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Card>

          {/* Espaço para a barra de salvar não cobrir o último item */}
          {alterados.length > 0 && <Box sx={{ height: 88 }} />}
        </>
      )}

      {alterados.length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: { xs: `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom))`, md: 0 },
            zIndex: (theme) => theme.zIndex.appBar,
            borderRadius: 0,
            px: 2,
            py: 1.25,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: 600, mx: 'auto' }}>
            <Typography variant="body2" sx={{ flexGrow: 1 }}>
              {alterados.length} alteraç{alterados.length === 1 ? 'ão' : 'ões'} para salvar
            </Typography>
            <Button color="inherit" onClick={() => { setEdicao({}); setEdicaoGoleiros({}); }} disabled={salvando}>Descartar</Button>
            <Button variant="contained" startIcon={<SaveRoundedIcon />} onClick={salvar} disabled={salvando}>Salvar</Button>
          </Stack>
        </Paper>
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        {toast ? <Alert variant="filled" severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </AppLayout>
  );
};

export default PeladaEstatisticasPage;
